import * as Clipboard from "clipboard-polyfill";
import React, { FC, useEffect, useMemo, useState } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeveloperBoardIcon from "@mui/icons-material/DeveloperBoard";

import { AGDialog } from "../../../components/AGDialog";
import { AGDataGrid } from "../../../components/AGDataGrid";
import { GridColDef, GridPaginationModel, GridRenderCellParams } from "@mui/x-data-grid";
import { useGetDeviceCamerasListQuery } from "../../../redux/reducers/configuration.reducer";
import { IDeviceCameraDetails, IGetDeviceCamerasListRequestData } from "../../../types";
import { ROWS_PER_PAGE } from "../../../utils/constants";
import "../index.css";

interface ICameraSettingsDialogProps {
	open: boolean;
	deviceId: number;
	deviceName: string;
	onClose: () => void;
}

const CameraSettingsDialog: FC<ICameraSettingsDialogProps> = (props) => {
	const { open, deviceId, deviceName, onClose } = props;

	const [showCopyRtspButton, setShowCopyRtspButton] = useState<boolean>(true);
	const [filters, setFilters] = useState<IGetDeviceCamerasListRequestData>({
		page: 1,
		device: -1
	});

	// APIS
	// GET DEVICE CAMERAS LIST
	const {
		data: getDeviceCamerasResponse,
		isLoading: getDeviceCamerasLoading,
		isFetching: getDeviceCamerasFetching
	} = useGetDeviceCamerasListQuery(filters, { skip: filters.device < 0 });

	const rowsData = useMemo(
		() => (getDeviceCamerasResponse ? getDeviceCamerasResponse.results : []),
		[getDeviceCamerasResponse]
	);

	function handlePageChange(event: GridPaginationModel) {
		setFilters((currentFilters) => ({
			...currentFilters,
			page: event.page + 1
		}));
	}

	function handleCopyRTSP(rtsp: string) {
		if (rtsp) {
			Clipboard.writeText(rtsp)
				.then(() => {
					setShowCopyRtspButton(false);

					setTimeout(() => setShowCopyRtspButton(true), 1000);
				})
				.catch((error) => console.error("UNABLE TO COPY RTSP URL ::", error));
		}
	}

	const columnsData: GridColDef[] = [
		{
			field: "site_name",
			headerName: "Camera",
			flex: 0.5,
			minWidth: 120,
			sortable: false
		},
		{
			field: "site_id",
			headerName: "Site ID",
			width: 100,
			sortable: false
		},
		{
			field: "camera_id",
			headerName: "Camera ID",
			width: 100,
			sortable: false
		},
		{
			field: "rtsp_uri",
			headerName: "RTSP",
			flex: 1,
			minWidth: 200,
			sortable: false,
			renderCell: (params: GridRenderCellParams<IDeviceCameraDetails, string>) => (
				<Box
					sx={{
						width: "100%",
						height: "100%",
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						gap: "0.5rem",
						overflow: "hidden"
					}}
				>
					<Typography variant="body2" whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
						{params.value}
					</Typography>

					{showCopyRtspButton ? (
						<IconButton size="small" onClick={() => handleCopyRTSP(params.value ?? "")}>
							<ContentCopyIcon fontSize="inherit" />
						</IconButton>
					) : (
						<Tooltip open title="Copied!" placement="top">
							<IconButton disabled size="small">
								<CheckCircleIcon fontSize="small" color="success" />
							</IconButton>
						</Tooltip>
					)}
				</Box>
			)
		}
	];

	useEffect(() => {
		setFilters((currentFilters) => ({
			...currentFilters,
			page: 1,
			device: deviceId
		}));
	}, [deviceId]);

	return (
		<AGDialog open={open} onClose={onClose} color="primary" title="Connected Cameras">
			<Box className="camera-settings-dialog-content-wrapper">
				<Box className="camera-settings-header-wrapper">
					<Box className="camera-settings-device-name-wrapper">
						<DeveloperBoardIcon />
						<Typography fontWeight={500}>{deviceName}</Typography>
					</Box>
				</Box>

				<Box className="camera-settings-table-wrapper">
					<AGDataGrid
						columns={columnsData}
						rows={rowsData}
						paginationMode="server"
						paginationModel={{ page: filters.page - 1, pageSize: ROWS_PER_PAGE }}
						onPaginationModelChange={handlePageChange}
						rowCount={getDeviceCamerasResponse?.count ?? 0}
						loading={getDeviceCamerasLoading || getDeviceCamerasFetching}
						hideFooter={(getDeviceCamerasResponse?.count ?? 0) <= ROWS_PER_PAGE}
						sx={{ minHeight: "91px" }}
					/>
				</Box>
			</Box>
		</AGDialog>
	);
};

export default CameraSettingsDialog;
